<script setup lang="ts">
  import { storeToRefs } from "pinia";
  import { computed } from "vue";
  import { Plus, Trash2, TriangleAlert, X } from "lucide-vue-next";
  import { Select } from "@/components/episto-ui/select";
  import { Input } from "@/components/ui/input";
  import { Button } from "@/components/ui/button";
  import { useConditionsStore } from "@stores/survey/conditions/conditions";
  import { cardTokenToOption } from "@utils/computeCardOptions";
  import { isComparisonType } from "@utils/conditionOperators";
  import type { Condition } from "@/types";

  const conditionsStore = useConditionsStore();
  const { cardTokenOptions, operatorOptions } = storeToRefs(conditionsStore);

  const {
    handleAddCondition,
    removeCondition,
    valuesOptions,
    allowCreateValuesOptions,
    typeOptionsFor,
    valueInputFor,
    handleCardTokenUpdate,
    updateType,
    updateValues,
    updateValue,
    updateBound,
    updateOperator,
  } = conditionsStore;

  const picksScalePoint = (condition: Condition) =>
    isComparisonType(condition.type) && valueInputFor(condition) === "scale";

  const props = defineProps<{
    conditions: Condition[];
    groupIndex?: number;
    allowMultiple?: boolean;
    allowDeleteAll?: boolean;
    disableAll?: boolean;
    twoRows?: boolean;
    elementName?: string | null;
    elementIndex?: number;
    reverseQuestionsOptions?: boolean;
  }>();

  const emit = defineEmits<{
    removeConditionsGroup: [groupIndex: number];
  }>();

  const cardTokenOptionsOrdered = computed(() => {
    return props.reverseQuestionsOptions ? [...cardTokenOptions.value].reverse() : cardTokenOptions.value;
  });
  const cardTokenModelArray = computed(() => {
    return props.conditions.map((condition) => cardTokenToOption(condition?.card_token, true));
  });
  const canDelete = computed(() => props.allowDeleteAll || (props.conditions?.length ?? 0) > 1);

  const hintFor = (condition: Condition) => {
    if (condition.card_token == null) return null;
    const optionValue = cardTokenToOption(condition.card_token, true);
    if (!cardTokenOptions.value.some((option: { value: unknown }) => option.value === optionValue)) {
      return { text: "This question is not asked before this card, so the condition can never be met.", error: true };
    }
    if (condition.type === "between") {
      if (condition.min == null || condition.max == null) return { text: "Set both bounds.", error: false };
      return null;
    }
    if (isComparisonType(condition.type)) {
      if (condition.value == null || condition.value === "") return { text: "Pick a value.", error: false };
      return null;
    }
    return null;
  };
  const hints = computed(() => props.conditions.map(hintFor));
</script>

<template>
  <div :class="allowMultiple && 'overflow-hidden rounded-lg border border-border/60 bg-background'">
    <div
      v-if="allowMultiple"
      class="flex items-center justify-between border-b border-border/60 bg-muted/40 px-3 py-1">
      <span class="text-xs text-muted-foreground">Group {{ (groupIndex ?? 0) + 1 }}</span>
      <Button
        variant="ghost"
        size="icon"
        class="text-foreground-subtle hover:text-destructive"
        :disabled="disableAll"
        @click="emit('removeConditionsGroup', groupIndex ?? 0)">
        <Trash2 class="size-4" />
      </Button>
    </div>

    <div
      v-if="conditions?.length > 0"
      class="space-y-1.5"
      :class="allowMultiple && 'px-3 pt-2'">
      <div
        v-for="(condition, index) in conditions"
        :key="`group-${groupIndex}-condition-${index}`"
        class="space-y-1">
        <div
          class="flex items-center gap-2"
          :class="twoRows && 'flex-wrap'">
          <div :class="twoRows ? 'flex min-w-0 flex-1 items-center gap-2' : 'contents'">
            <div
              v-if="!twoRows || index > 0"
              class="w-20 shrink-0">
              <span
                v-if="index === 0"
                class="block px-3 text-13 font-semibold text-primary">
                If
              </span>
              <Select
                v-else
                :model-value="conditions[index - 1].operator"
                :options="operatorOptions"
                :disabled="disableAll"
                class="h-9 font-semibold text-primary"
                placeholder="Or"
                @update:model-value="updateOperator(conditions[index - 1], $event)" />
            </div>
            <div
              class="min-w-0"
              :class="twoRows ? 'flex-1' : 'flex-[5]'">
              <Select
                :model-value="cardTokenModelArray[index]"
                :options="cardTokenOptionsOrdered"
                :disabled="disableAll"
                filterable
                class="h-9"
                placeholder="Question"
                @update:model-value="handleCardTokenUpdate(condition, $event)" />
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon-sm"
            class="shrink-0 text-foreground-subtle hover:text-primary"
            :class="!twoRows && 'order-last'"
            :disabled="disableAll || !canDelete"
            aria-label="Remove this condition"
            @click="removeCondition(conditions, index)">
            <X class="size-3.5" />
          </Button>
          <div :class="twoRows ? 'flex w-full items-start gap-2' : 'contents'">
            <div
              class="shrink-0"
              :class="twoRows ? 'w-22' : 'w-24'">
              <Select
                :model-value="condition.type"
                :options="typeOptionsFor(condition)"
                :disabled="disableAll"
                class="h-9"
                placeholder="Is"
                @update:model-value="updateType(condition, $event)" />
            </div>
            <Select
              v-if="picksScalePoint(condition)"
              :model-value="condition.value ?? null"
              :options="valuesOptions(condition)"
              :disabled="disableAll"
              placeholder="Value"
              class="h-9 min-w-0"
              :class="twoRows ? 'flex-1' : 'flex-[4]'"
              @update:model-value="updateValue(condition, $event)" />
            <Input
              v-else-if="isComparisonType(condition.type)"
              type="number"
              step="any"
              :model-value="condition.value ?? ''"
              :disabled="disableAll"
              placeholder="Value"
              class="h-9 min-w-0"
              :class="twoRows ? 'flex-1' : 'flex-[4]'"
              @update:model-value="updateValue(condition, $event)" />
            <template v-else-if="condition.type === 'between'">
              <Input
                type="number"
                step="any"
                :model-value="condition.min ?? ''"
                :disabled="disableAll"
                placeholder="Min"
                class="h-9 min-w-0"
                :class="twoRows ? 'flex-1' : 'flex-[1.5]'"
                @update:model-value="updateBound(condition, 'min', $event)" />
              <Input
                type="number"
                step="any"
                :model-value="condition.max ?? ''"
                :disabled="disableAll"
                placeholder="Max"
                class="h-9 min-w-0"
                :class="twoRows ? 'flex-1' : 'flex-[1.5]'"
                @update:model-value="updateBound(condition, 'max', $event)" />
            </template>
            <Select
              v-else
              :model-value="condition.values || []"
              :options="valuesOptions(condition)"
              :disabled="disableAll"
              placeholder="Select value(s)"
              multiple
              :creatable="allowCreateValuesOptions(condition)"
              filterable
              class="min-h-9 min-w-0"
              :class="twoRows ? 'flex-1' : 'flex-[4]'"
              @update:model-value="updateValues(condition, $event)" />
          </div>
        </div>

        <p
          v-if="hints[index]"
          class="flex items-center gap-1.5 text-xs"
          :class="[hints[index]?.error ? 'text-destructive' : 'text-muted-foreground', !twoRows && 'pl-22']">
          <TriangleAlert
            v-if="hints[index]?.error"
            class="size-3.5 shrink-0" />
          {{ hints[index]?.text }}
        </p>
      </div>
    </div>

    <div
      :class="[allowMultiple ? (conditions?.length > 0 ? 'px-3 pb-3 pt-1' : 'p-3') : conditions?.length > 0 && 'pt-1']">
      <div :class="!twoRows && conditions?.length > 0 && 'ml-22'">
        <Button
          variant="outline"
          size="sm"
          :disabled="disableAll"
          @click="handleAddCondition(conditions, elementName, elementIndex)">
          <Plus class="size-3.5" />
          Add condition
        </Button>
      </div>
    </div>
  </div>
</template>
